import {reporter} from '../error-reporting';

export const enum UrlFormat {
  Django = `django`, // /report/<projectId>/<appName> or /report/<projectId>/view/<workspaceId>/<appName>
  Volt = `volt`, // /project/<projectId>/app/<appName> or /project/<projectId>/view/<workspaceId>/app/<appName>
}

export interface UrlParsedState {
  urlFormat: UrlFormat;
  projectId: string | null;
  workspaceId: string | null;
  appName: string; // e.g insights, flows (a.k.a tab, reportName, report)
}

/**
 * These apps don't work inside views, we should ignore workspaceId when creating paths for them.
 */
export const PROJECT_ONLY_APPS = new Set([`views`, `library`]);

/**
 * @returns null if url state can't be inferred from href
 */
export function parseStateFromUrl(pathname: string = window.location.pathname): UrlParsedState | null {
  if (/^https?:/.test(pathname)) {
    // full url is passed, extract pathname from it
    pathname = new URL(pathname).pathname;
  }

  // TODO @noj: remove when legacy workspace urls are redirected to new /view based pattern in django
  const djangoWorkspaceMatch = /^\/report\/workspace\/(\d+)\/([a-z-_]+)/.exec(pathname);
  if (djangoWorkspaceMatch) {
    return {
      urlFormat: UrlFormat.Django,
      projectId: null,
      workspaceId: djangoWorkspaceMatch[1],
      appName: djangoWorkspaceMatch[2],
    };
  }

  const djangoViewMatch = /^\/report\/(\d+)\/view\/(\d+)\/([a-z-_]+)/.exec(pathname);
  if (djangoViewMatch) {
    return {
      urlFormat: UrlFormat.Django,
      projectId: djangoViewMatch[1],
      workspaceId: djangoViewMatch[2],
      appName: djangoViewMatch[3],
    };
  }

  const djangoProjectMatch = /^\/report\/(\d+)\/([a-z-_]+)/.exec(pathname);
  if (djangoProjectMatch) {
    return {
      urlFormat: UrlFormat.Django,
      projectId: djangoProjectMatch[1],
      workspaceId: null,
      appName: djangoProjectMatch[2],
    };
  }

  const voltViewMatch = /^\/project\/(\d+)\/view\/(\d+)\/app\/([a-z-]+)/.exec(pathname);
  if (voltViewMatch) {
    return {
      urlFormat: UrlFormat.Volt,
      projectId: voltViewMatch[1],
      workspaceId: voltViewMatch[2],
      appName: voltViewMatch[3],
    };
  }

  const voltProjectMatch = /^\/project\/(\d+)\/app\/([a-z-_]+)/.exec(pathname);
  if (voltProjectMatch) {
    return {
      urlFormat: UrlFormat.Volt,
      projectId: voltProjectMatch[1],
      workspaceId: null,
      appName: voltProjectMatch[2],
    };
  }

  return null;
}

/**
 * returns a path like /report/<pid>/<app> or /project/<pid>/view/<wid>/app/<app>
 */
export function makePathFromUrlState(newUrlState: Partial<UrlParsedState>): string {
  const curUrlState = parseStateFromUrl();
  let {urlFormat, workspaceId, projectId, appName} = {...curUrlState, ...newUrlState};
  let path = ``;

  if (PROJECT_ONLY_APPS.has(appName)) {
    workspaceId = ``;
  }

  if (urlFormat === UrlFormat.Django) {
    if (workspaceId) {
      if (projectId) {
        path = `/report/${projectId}/view/${workspaceId}/${appName}`;
      } else {
        // legacy url pattern, when we don't know the projectId
        path = `/report/workspace/${workspaceId}/${appName}`;
      }
    } else {
      path = `/report/${projectId}/${appName}`;
    }
  } else if (urlFormat === UrlFormat.Volt) {
    if (workspaceId) {
      path = `/project/${projectId}/view/${workspaceId}/app/${appName}`;
    } else {
      path = `/project/${projectId}/app/${appName}`;
    }
  } else {
    reporter.warn(
      new Error(`makePathFromUrlState cannot create path from urlFormat:${urlFormat}, must be 'django' or 'volt'`),
      urlFormat,
    );
  }

  return path;
}
